import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import NotApplicable from 'components/Form/NotApplicable';
import Text from 'components/Form/Text';
import { FinancialNonPaymentItemModel } from '../../../../forms/sections/financial/nonPayment/types';
import { ErrorCode } from '../../../../redux/modules/application/reducers/error';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
} from '../../../../types/props';
import { ValidationElementProps } from '../../../Form/ValidationElement/ValidationElement';
import Infractions from './Infractions';

interface Props extends Partial<FinancialNonPaymentItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class NonpaymentItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialNonPaymentItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Infractions: this.props.Infractions,
      AccountNumber: this.props.AccountNumber,
      PropertyType: this.props.PropertyType,
      Amount: this.props.Amount,
      AmountEstimated: this.props.AmountEstimated,
      Reason: this.props.Reason,
      Status: this.props.Status,
      Date: this.props.Date,
      ResolvedNotApplicable: this.props.ResolvedNotApplicable,
      Resolved: this.props.Resolved,
      Description: this.props.Description,
      AccountNumberNotApplicable: this.props.AccountNumberNotApplicable,
      AccountNumberExplanation: this.props.AccountNumberExplanation,
      ...queue,
    });
  };

  updateName = (values: FinancialNonPaymentItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateInfractions = (values: FinancialNonPaymentItemModel['Infractions']) => {
    this.update({
      Infractions: values,
    });
  };

  updateAccountNumber = (values: FinancialNonPaymentItemModel['AccountNumber']) => {
    this.update({
      AccountNumber: values,
    });
  };

  updatePropertyType = (values: FinancialNonPaymentItemModel['PropertyType']) => {
    this.update({
      PropertyType: values,
    });
  };

  updateAmount = (values: FinancialNonPaymentItemModel['Amount']) => {
    this.update({
      Amount: values,
    });
  };

  updateAmountEstimated = (values: FinancialNonPaymentItemModel['AmountEstimated']) => {
    this.update({
      AmountEstimated: values,
    });
  };

  updateReason = (values: FinancialNonPaymentItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateStatus = (values: FinancialNonPaymentItemModel['Status']) => {
    this.update({
      Status: values,
    });
  };

  updateDate = (values: FinancialNonPaymentItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateResolved = (values: FinancialNonPaymentItemModel['Resolved']) => {
    this.update({
      Resolved: values,
    });
  };

  updateResolvedNotApplicable = (values: FinancialNonPaymentItemModel['ResolvedNotApplicable']) => {
    this.update({
      Resolved: values.applicable ? this.props.Resolved : {},
      ResolvedNotApplicable: values,
    });
  };

  updateDescription = (values: FinancialNonPaymentItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateAccountNumberNotApplicable = (value: FinancialNonPaymentItemModel['AccountNumberNotApplicable']) => {
    this.update({
      AccountNumber: value.applicable ? this.props.AccountNumber : {},
      AccountNumberNotApplicable: value,
      AccountNumberExplanation: value.applicable ? {} : this.props.AccountNumberExplanation,
    });
  };

  updateAccountNumberExplanation = (values: FinancialNonPaymentItemModel['AccountNumberExplanation']) => {
    this.update({
      AccountNumberExplanation: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div className="nonpayment-item">
        <Field
          title={i18n.t('financial.nonpayment.heading.name')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
        >
          <Text
            name="Name"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.props.onError}
            className="nonpayment-name"
            required={this.props.required}
            attribute={`${attribute}.Name`}
          />
        </Field>

        <Field
          title={i18n.t('financial.nonpayment.heading.infractions')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Infractions`}
        >
          <Infractions
            name="Infractions"
            {...this.props.Infractions}
            onUpdate={this.updateInfractions}
            onError={this.props.onError}
            className="nonpayment-infractions"
            required={this.props.required}
            attribute={`${attribute}.Infractions`}
          />
        </Field>

        <Field
          title={i18n.t('financial.nonpayment.heading.accountnumber')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AccountNumber`}
          className="minimal-margin-bottom"
          exactAttribute
        >
          <NotApplicable
            name="AccountNumberNotApplicable"
            className="na-accountnumber"
            label={i18n.t('financial.nonpayment.label.idk')}
            or={i18n.t('financial.nonpayment.para.or')}
            {...this.props.AccountNumberNotApplicable}
            onUpdate={this.updateAccountNumberNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.AccountNumberNotApplicable`}
            includeNAConfirmation={Boolean(this.props.AccountNumber?.value || this.props.AccountNumberExplanation?.value)}
          >
            <Text
              name="AccountNumber"
              {...this.props.AccountNumber}
              onUpdate={this.updateAccountNumber}
              onError={this.props.onError}
              className="nonpayment-accountnumber"
              required={this.props.required}
              attribute={`${attribute}.AccountNumber`}
            />
          </NotApplicable>
        </Field>
        {this.props.AccountNumberNotApplicable?.applicable === false && (
          <Field
            title={i18n.t('financial.nonpayment.heading.accountNumberExplanation')}
            attribute={`${attribute}.AccountNumberExplanation`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              title="AccountNumberExplanation"
              className="explanation-comment"
              attribute={`${attribute}.AccountNumberExplanation`}
              {...this.props.AccountNumberExplanation}
              onUpdate={this.updateAccountNumberExplanation}
              required={this.props.required}
              onError={this.props.onError}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.nonpayment.heading.propertytype')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PropertyType`}
        >
          <Text
            name="PropertyType"
            {...this.props.PropertyType}
            onUpdate={this.updatePropertyType}
            onError={this.props.onError}
            className="nonpayment-propertytype"
            required={this.props.required}
            attribute={`${attribute}.PropertyType`}
          />
        </Field>

        <Field
          title={i18n.t('financial.nonpayment.heading.amount')}
          className="nonpayment-amount-notapplicable"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Amount`}
        >
          <div>
            <Currency
              name="Amount"
              {...this.props.Amount}
              onUpdate={this.updateAmount}
              onError={this.props.onError}
              className="nonpayment-amount"
              min="1"
              required={this.props.required}
              attribute={`${attribute}.Amount`}
            />
            <div className="flags">
              <Checkbox
                name="AmountEstimated"
                label={i18n.t('financial.nonpayment.label.estimated')}
                toggle="false"
                onUpdate={this.updateAmountEstimated}
                onError={this.props.onError}
                {...this.props.AmountEstimated}
                attribute={`${attribute}.AmountEstimated`}
              />
            </div>
          </div>
        </Field>

        <Field
          title={i18n.t('financial.nonpayment.heading.reason')}
          scrollIntoView={this.props.scrollIntoView}
          help="financial.nonpayment.help.reason"
          attribute={`${attribute}.Reason`}
        >
          <Textarea
            name="Reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            className="nonpayment-reason"
            required={this.props.required}
            attribute={`${attribute}.Reason`}
          />
        </Field>

        <Field
          title={i18n.t('financial.nonpayment.heading.status')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Status`}
        >
          <Text
            name="Status"
            {...this.props.Status}
            onUpdate={this.updateStatus}
            onError={this.props.onError}
            className="nonpayment-status"
            required={this.props.required}
            attribute={`${attribute}.Status`}
          />
        </Field>

        <Field
          title={i18n.t('financial.nonpayment.heading.date')}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="nonpayment-date"
            hideDay
            required={this.props.required}
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('financial.nonpayment.heading.resolved')}
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          attribute={`${attribute}.Resolved`}
        >
          <NotApplicable
            name="ResolvedNotApplicable"
            label={i18n.t('financial.nonpayment.label.notresolved')}
            or={i18n.m('financial.nonpayment.para.or')}
            {...this.props.ResolvedNotApplicable}
            onUpdate={this.updateResolvedNotApplicable}
            onError={this.props.onError}
            attribute={`${attribute}.ResolvedNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Resolved?.month || this.props.Resolved?.year)}
          >
            <DateControl
              name="Resolved"
              {...this.props.Resolved}
              minDate={this.props.Date}
              minDateEqualTo
              prefix="nonPayment"
              onUpdate={this.updateResolved}
              onError={this.props.onError}
              className="nonpayment-resolved"
              hideDay
              required={this.props.required}
              attribute={`${attribute}.Resolved`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.nonpayment.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          help="financial.nonpayment.help.description"
          attribute={`${attribute}.Description`}
        >
          <Textarea
            name="Description"
            {...this.props.Description}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            className="nonpayment-description"
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>
      </div>
    );
  }
}

NonpaymentItem.defaultProps = {
  ResolvedNotApplicable: { applicable: true },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
  required: false,
};
